
import { Vue, Component } from 'vue-property-decorator';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import TechnologistOrderFormInputs from '@/components/views/NewProjectView/Order/TechnologistOrderFormInputs.vue';
import TechnologistOrderFormOrders from '@/components/views/NewProjectView/Order/TechnologistOrderFormOrders.vue';
import TechnologistOrderFormDates from '@/components/views/NewProjectView/Order/TechnologistOrderFormDates.vue';
import { TechnologistFormOrder } from '@/interfaces/components/projectNew/TechnologistFormOrder';
import { technologistOrderFormConfiguration, prepareTechnologistOrderFormData, refreshTechnologistOrderFormOptions } from '@/helpers/OrderHelper';
import { TechnologistFormField } from '@/interfaces/components/projectNew/TechnologistFormField';
import Offer from '@/models/Offer';
import TechnologistOrder from '@/models/TechnologistOrder';
import InternalOrder from '@/models/InternalOrder';
import { UserRepository } from '@/repositories/UserRepository';
import ProductSystem from '@/models/ProductSystem';
import { ProductSystemRepository } from '@/repositories/ProductSystemRepository';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { TechnologistForm } from '@/interfaces/components/projectNew/TechnologistForm';
import TechnologistOrderAttachmentAPI from '@/api/TechnologistOrderAttachmentAPI';
import ProjectInternalOrderAttachmentAPI from '@/api/ProjectInternalOrderAttachmentAPI';
import { hasCommentOrAttachment } from '@/helpers/OrderValidationHelper';
import ProjectPreorderAPI from '@/api/ProjectPreorderAPI';
import ProjectPreorderAttachmentAPI from '@/api/ProjectPreorderAttachmentAPI';
import ProjectModel from '@/models/Project';
import { formatDateToDateString } from '@/helpers/LocaleHelper';
import { notification } from 'ant-design-vue';
import moment from 'moment';
import { getFirstAvailableDeliveryDate } from '@/helpers/DeliveryDateHelper';
import NextStep from '@/models/NextStep';
import { AxiosResponse } from 'axios';
import { orderAttachmentTypeOptions } from '@/enums/global/OrderAttachmentType';
import AttachmentAPI from '@/api/AttachmentAPI';
// @ts-ignore
import downloadFile from 'downloadjs';

@Component({
    name: 'PreorderReviewPopup',
    components: { TechnologistOrderFormInputs, TechnologistOrderFormOrders, TechnologistOrderFormDates },
})
export default class PreorderReviewPopup extends Vue {
    private visible = false;
    private preorder: any = null;
    private project: any = null;

    private formData: Partial<Record<keyof TechnologistForm, any>> = {};
    private orders: TechnologistFormOrder[] = [];
    private errors: { [key: string]: string } = {};
    private orderErrors: { [key: string]: { system: boolean; quantity: boolean } } = {};
    private loadingOverlay = new LoadingOverlayHelper(this, {});
    private pendingColorTarget: 'general' | number | null = null;

    private get orderFormConfiguration() {
        return technologistOrderFormConfiguration;
    }

    private get systems() {
        return ProductSystemRepository.getAllShallow();
    }

    private get preorderAttachments() {
        return this.preorder?.attachments ?? [];
    }

    private get currentUser() {
        return UserRepository.getCurrentUser();
    }

    private async downloadClientAttachment(attachment: any) {
        let blob;
        try {
            blob = await AttachmentAPI.getBlob(attachment.attachment);
        } catch (e) {
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Something went wrong downloading the attachment') as string,
            });
            return;
        }
        downloadFile(blob.data, attachment.displayName);
    }

    private formatDate(date: string) {
        return formatDateToDateString(date);
    }

    private attachmentTypeLabel(value: string | null): string {
        if (!value) {
            return '';
        }
        const option = orderAttachmentTypeOptions.find((o) => o.value === value);
        return option ? (this.$t(option.label) as string) : '';
    }

    private get workFreeDays(): any[] {
        return this.$store.getters['tableData/workFreeDays'] ?? [];
    }

    private async loadWorkFreeDays() {
        if (this.workFreeDays.length) {
            return;
        }
        try {
            await NextStep.getWorkFreeDays();
        } catch (e) {
            // Without holidays the default is still today + 3 weeks.
        }
    }

    private selectedOffer: any = null;

    private async openPopup(payload: { preorderId: string; project: any }) {
        this.resetPopupState();
        this.loadingOverlay.start();
        try {
            await refreshTechnologistOrderFormOptions();
            this.preorder = await ProjectPreorderAPI.getById(payload.preorderId);
            this.project = payload.project;
            this.selectedOffer = this.project?.offerRevisions?.length
                ? this.project.offerRevisions[this.project.offerRevisions.length - 1]
                : null;
            await this.loadWorkFreeDays();
            this.prefillForm();
            this.visible = true;
            await this.$nextTick();
            const inputsRef = this.$refs.orderFormInputs as any;
            if (inputsRef?.setPrefilledAttachmentRows) {
                inputsRef.setPrefilledAttachmentRows((this.formData as any).attachmentRows ?? []);
            }
        } catch (e) {
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Could not load preorder details') as string,
            });
        } finally {
            this.loadingOverlay.stop();
        }
    }

    private prefillForm() {
        this.formData = technologistOrderFormConfiguration.formFields.reduce((acc: Record<string, any>, field) => {
            if (field.type === 'number') {
                acc[field.name as keyof TechnologistForm] = 0;
            } else if (field.type === 'textarea' || field.type === 'text') {
                acc[field.name as keyof TechnologistForm] = '';
            } else if (field.type === 'dropdown') {
                acc[field.name as keyof TechnologistForm] = null;
            } else if (field.type === 'attachment') {
                acc[field.name as keyof TechnologistForm] = [];
            } else if (field.type === 'date') {
                acc[field.name as keyof TechnologistForm] = null;
            }
            return acc;
        }, {} as Partial<TechnologistForm>);

        if (this.preorder?.comment) {
            (this.formData as any).comment = this.preorder.comment;
        }

        (this.formData as any).orderAcceptedDate = moment();
        (this.formData as any).deliveryDate = getFirstAvailableDeliveryDate(this.workFreeDays);
        if (this.preorder?.desiredDeliveryDate) {
            (this.formData as any).deliveryDate = moment(this.preorder.desiredDeliveryDate);
        }

        if (this.preorder?.prefilledDocumentType) {
            (this.formData as any).documentType = this.preorder.prefilledDocumentType;
        }

        if (this.preorder?.prefilledTechnologistName) {
            (this.formData as any).technologistName = this.preorder.prefilledTechnologistName;
        }

        const preorderAttachmentRows = this.mapExistingAttachmentsToRows(this.preorder?.attachments);
        (this.formData as any).attachmentRows = preorderAttachmentRows;
        (this.formData as any).attachments = preorderAttachmentRows.map((row: any) => row.file).filter(Boolean);

        if (this.preorder?.positions && this.preorder.positions.length > 0) {
            this.orders = this.preorder.positions.map((position: any) => ({
                system: this.matchSystemName(position.system),
                requestedGroup: position.system,
                quantity: position.quantity,
                attachmentRows: this.mapExistingAttachmentsToRows(position.attachments),
                colorData: null,
            }));
            (this.formData as any).numOfOrders = this.orders.length.toString();
        } else {
            this.orders = [{ system: '', quantity: 0, attachmentRows: [{ file: null, attachmentType: null }], colorData: null }];
            (this.formData as any).numOfOrders = '1';
        }
    }

    private matchSystemName(requested: string): string {
        const needle = (requested ?? '').trim().toLowerCase();
        const match = ProductSystemRepository.getAllShallow().find(
            (system) => (system.name ?? '').trim().toLowerCase() === needle,
        );

        return match ? match.name : '';
    }

    private mapExistingAttachmentsToRows(attachments: any[] | undefined): any[] {
        const rows: any[] = (attachments ?? []).map((attachment: any) => ({
            file: {
                uid: `existing-${attachment.id}`,
                name: attachment.displayName,
                status: 'done',
                url: attachment.attachment,
            },
            attachmentType: attachment.documentType ?? null,
            existingAttachmentId: attachment.id,
        }));
        return rows.length > 0 ? rows : [{ file: null, attachmentType: null }];
    }

    private closePopup() {
        this.visible = false;
    }
    private resetPopupState() {
        this.visible = false;
        this.preorder = null;
        this.project = null;
        this.formData = {};
        this.orders = [];
        this.errors = {};
        this.orderErrors = {};
        this.pendingColorTarget = null;
    }

    private openColorPicker(payload: { target: 'general' | number }) {
        this.pendingColorTarget = payload.target;
        EventBus.$emit(EventBusEvents.showColorModule, {
            colorSystemPID: 'material',
            fieldValue: '',
            activeProductFormId: null,
            productName: '',
        });
    }

    private onColorSelected(payload: { color: any; selectedPowder: any }) {
        if (this.pendingColorTarget === null) {
            return;
        }
        const colorData = {
            colorName: payload.color?.name ?? null,
            colorHex: payload.color?.hex ?? null,
            powderId: payload.selectedPowder?.id ? String(payload.selectedPowder.id) : null,
            powderName: payload.selectedPowder?.name ?? null,
            ingeniousColor: payload.color?.ingeniousColor ?? null,
        };
        if (this.pendingColorTarget === 'general') {
            this.$set(this.formData, 'colorData', colorData);
        } else {
            this.$set(this.orders[this.pendingColorTarget], 'colorData', colorData);
        }
        this.pendingColorTarget = null;
    }

    private validateField(field: TechnologistFormField) {
        if (!field.required) {
            delete this.errors[field.name];
            return;
        }
        const value = this.formData[field.name as keyof TechnologistForm];
        let isValid = true;
        if (field.type === 'number' && value === 0) {
            isValid = false;
        } else if ((field.type === 'text' || field.type === 'textarea') && typeof value === 'string' && !value.trim()) {
            isValid = false;
        } else if ((field.type === 'dropdown' || field.type === 'date') && (value === null || value === undefined)) {
            isValid = false;
        } else if (field.type === 'attachment' && Array.isArray(value) && value.length === 0) {
            isValid = false;
        }

        if (!isValid) {
            this.$set(this.errors, field.name, `${this.$t(field?.errorFieldName ?? field.label)}`);
        } else {
            this.$delete(this.errors, field.name);
        }
    }

    private hasAttachmentWithoutType(): boolean {
        const missingType = (row: any) => row.file && !row.existingAttachmentId && !row.attachmentType;
        const orderLevelRows = (this.formData as any).attachmentRows ?? [];
        return (
            orderLevelRows.some(missingType) ||
            this.orders.some((order) => (order.attachmentRows ?? []).some(missingType))
        );
    }

    private validateForm(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.orderFormConfiguration.formFields.forEach((field) => {
                if (field.required) this.validateField(field);
            });

            if (!hasCommentOrAttachment(this.formData)) {
                this.$set(this.errors, 'commentOrAttachment', this.$t('Please add at least a comment or an attachment.') as string);
            } else {
                this.$delete(this.errors, 'commentOrAttachment');
            }

            if (this.hasAttachmentWithoutType()) {
                this.$set(this.errors, 'attachmentType', this.$t('Attachment type') as string);
            } else {
                this.$delete(this.errors, 'attachmentType');
            }

            this.orders.forEach((order, i) => {
                if (!order.system || order.quantity === 0) {
                    this.$set(this.orderErrors, i.toString(), {
                        system: !order.system,
                        quantity: order.quantity === 0,
                    });
                } else {
                    this.$delete(this.orderErrors, i.toString());
                }
            });

            if (Object.keys(this.orderErrors).length > 0) {
                notification.error({
                    message: this.$t('Error') as string,
                    description: this.$t('Please fill all orders.') as string,
                });
                reject({ ...this.orderErrors });
            }

            if (Object.keys(this.errors).length === 0) {
                resolve();
            } else {
                const descriptionMessage = this.$t('Following fields are missing:') as string;
                const errorFields = Object.values(this.errors).join('\n-');
                notification.error({
                    message: this.$t('Error') as string,
                    description: `${descriptionMessage}\n-${errorFields}`,
                });
                reject({ ...this.errors });
            }
        });
    }

    private async submitForm() {
        if (!this.project || !this.preorder) {
            return;
        }
        let offerId;
        try {
            if (this.orders.length === 0) {
                notification.error({
                    message: this.$t('Error') as string,
                    description: this.$t('Please add at least one order.') as string,
                });
                return;
            }
            await this.validateForm();
        } catch (err) {
            console.error('Validation failed:', err);
            return;
        }

        try {
            this.loadingOverlay.start();

            if (!this.selectedOffer) {
                const projectId = this.project.id;
                offerId = (await Offer.createNew(String(projectId), this.project?.client?.paymentType?.id ?? null))?.data?.id as string;
                await Offer.getById(offerId);
                await this.$nextTick();
            } else {
                offerId = this.selectedOffer.id;
            }

            const formattedFormData = prepareTechnologistOrderFormData(this.formData);
            if ((this.formData as any).colorData) {
                (formattedFormData as any).technologistOrderColorData = (this.formData as any).colorData;
            }

            const technologistOrderFormRequest = await TechnologistOrder.createNew(
                formattedFormData,
                offerId,
                this.currentUser?.id ?? '1',
                String(this.preorder.id),
            );

            await this.uploadAttachments(String(technologistOrderFormRequest?.data?.id));
            await this.createOrders(offerId);
            await Offer.orderByTechnologist(offerId);

            const keptAttachmentIds = ((this.formData as any).attachmentRows ?? [])
            .map((row: any) => row.existingAttachmentId)
            .filter(Boolean)
            .map(String);
            
            const removedAttachmentIds = (this.preorder.attachments ?? [])
                .map((attachment: any) => String(attachment.id))
                .filter((attachmentId: string) => !keptAttachmentIds.includes(attachmentId));
            await ProjectPreorderAPI.release(String(this.preorder.id), removedAttachmentIds);

            notification.success({
                message: this.$t('Success') as string,
                description: this.$t('Preorder released successfully') as string,
            });

            EventBus.$emit(EventBusEvents.updatePendingPreorderCount);
            this.$emit('released');
        } catch (err) {
            console.error('Error releasing preorder', err);
            notification.error({
                message: this.$t('Error') as string,
                description: this.$t('Something went wrong releasing the preorder') as string,
            });
        } finally {
            EventBus.$emit(EventBusEvents.updateProject, { shouldUpdateOffer: true });
            this.loadingOverlay.stop();
            this.closePopup();
        }
    }

    private async uploadAttachments(technologistOrderId: string) {
        const attachmentRows = (this.formData as any).attachmentRows ?? [];
        const promises: Promise<any>[] = [];
        for (const row of attachmentRows) {
            if (row.existingAttachmentId) {
                promises.push(
                    ProjectPreorderAttachmentAPI.copyToOrder(String(row.existingAttachmentId), { technologistOrderId }),
                );
                continue;
            }
            const file = row.file?.originFileObj;
            if (!file) {
                continue;
            }
            promises.push(TechnologistOrderAttachmentAPI.upload(technologistOrderId, file, row.attachmentType ?? null));
        }
        try {
            await Promise.all(promises);
        } catch (error) {
            console.error(error);
        }
    }

    private async createOrders(newOfferId: string) {
        const promises = [];
        for (const order of this.orders as any[]) {
            promises.push(
                InternalOrder.createNew({ order, projectId: this.project.id, offerId: newOfferId }).then(
                    async (response: AxiosResponse) => {
                        const internalOrderId = (response as any)?.data?.id;
                        if (!internalOrderId) {
                            return;
                        }
                        if (order.colorData) {
                            await InternalOrder.updateColor(String(internalOrderId), order.colorData);
                        }
                        const attachmentRows = order.attachmentRows ?? [];
                        const attachmentPromises: Promise<any>[] = [];
                        for (const row of attachmentRows) {
                            if (row.existingAttachmentId) {
                                attachmentPromises.push(
                                    ProjectPreorderAttachmentAPI.copyToOrder(String(row.existingAttachmentId), {
                                        projectInternalOrderId: String(internalOrderId),
                                    }),
                                );
                                continue;
                            }
                            const file = row.file?.originFileObj;
                            if (!file) {
                                continue;
                            }
                            attachmentPromises.push(
                                ProjectInternalOrderAttachmentAPI.upload(String(internalOrderId), file, row.attachmentType ?? null),
                            );
                        }
                        await Promise.all(attachmentPromises);
                    },
                ),
            );
        }
        try {
            await Promise.all(promises);
        } catch (error) {
            console.error(error);
        }
    }

    private addOrder() {
        const order: TechnologistFormOrder = {
            system: '',
            quantity: 0,
            attachmentRows: [{ file: null, attachmentType: null }],
            colorData: null,
        };
        this.orders.push(order);
        (this.formData as any).numOfOrders = this.orders.length.toString();
    }

    private deleteOrder(index: number) {
        this.orders.splice(index, 1);
        (this.formData as any).numOfOrders = this.orders.length.toString();
        this.orderErrors.hasOwnProperty(index.toString()) && this.$delete(this.orderErrors, index.toString());
    }

    private onNumberOfOrdersChange() {
        if (!(this.formData as any).numOfOrders) {
            return;
        }
        const target = parseInt((this.formData as any).numOfOrders);
        if (this.orders.length === target) {
            return;
        }
        if (this.orders.length < target) {
            const difference = target - this.orders.length;
            for (let i = 0; i < difference; i++) {
                this.orders.push({
                    system: '',
                    quantity: 0,
                    attachmentRows: [{ file: null, attachmentType: null }],
                    colorData: null,
                });
            }
        }
    }

    private async fetchSystems() {
        if (this.systems.length < 2) {
            await ProductSystem.getAll();
        }
    }

    private created() {
        this.fetchSystems();
        EventBus.$on(EventBusEvents.showPreorderReviewPopup, this.openPopup);
        EventBus.$on(EventBusEvents.selectMaterialColor, this.onColorSelected);
    }

    private beforeDestroy() {
        EventBus.$off(EventBusEvents.showPreorderReviewPopup, this.openPopup);
        EventBus.$off(EventBusEvents.selectMaterialColor, this.onColorSelected);
    }
}
